import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Send, Loader2, Brain, Zap, MessageSquare } from "lucide-react";

export default function AdminChat() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [reasoning, setReasoning] = useState(false);
  const [lastUsage, setLastUsage] = useState(null);
  const endRef = useRef(null);

  useEffect(() => { if (!loading && (!user || !user.is_admin)) nav("/auth"); }, [user, loading, nav]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setBusy(true);
    try {
      const r = await api.post("/admin/chat", {
        session_id: sessionId,
        messages: next,
        complex_reasoning: reasoning,
      });
      setSessionId(r.data.session_id);
      setLastUsage({ model: r.data.model, ...r.data.usage });
      setMessages(m => [...m, { role: "assistant", content: r.data.reply }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Chat failed");
      setMessages(m => m.slice(0, -1)); // rollback the user message
    } finally { setBusy(false); }
  };

  if (!user?.is_admin) return null;

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="admin-chat-page">
      <div className="flex items-center gap-3 mb-2">
        <MessageSquare size={22} className="text-[color:var(--hb-royal)]"/>
        <h1 className="font-display text-4xl text-[color:var(--hb-navy)]">Assistant</h1>
      </div>
      <p className="text-sm text-neutral-600 mb-6">Private admin AI. Fully briefed on Hampton Blu / One United — knows your pricing, brand voice, DTF/puff rules, everything.</p>

      <div className="border border-neutral-200 bg-white min-h-[500px] flex flex-col" data-testid="chat-container">
        <div className="flex-1 p-5 overflow-y-auto max-h-[65vh]" data-testid="chat-messages">
          {messages.length === 0 ? (
            <div className="text-center py-16">
              <MessageSquare size={40} className="mx-auto text-neutral-300 mb-4"/>
              <p className="text-sm text-neutral-500">Ask me anything — pricing, design ideas, marketing copy, customer replies.</p>
              <div className="mt-6 flex flex-wrap gap-2 justify-center">
                {["Give me 3 fresh One United tee concepts","How should I price a bulk order of 30 hoodies?","Write a launch email for the Encore collection"].map((s, i) => (
                  <button key={i} onClick={() => setInput(s)} className="text-xs border border-neutral-300 px-3 py-1.5 hover:bg-neutral-50" data-testid={`suggestion-${i}`}>{s}</button>
                ))}
              </div>
            </div>
          ) : messages.map((m, i) => (
            <div key={i} className={`mb-4 flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}`}>
              <div className={`max-w-[80%] px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-[color:var(--hb-navy)] text-white" : "bg-neutral-100 text-neutral-900"}`}>
                {m.content}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start mb-4" data-testid="typing">
              <div className="bg-neutral-100 px-4 py-2.5 text-sm flex items-center gap-2">
                <Loader2 size={12} className="animate-spin"/> Thinking…
              </div>
            </div>
          )}
          <div ref={endRef}/>
        </div>

        <div className="border-t border-neutral-200 p-3">
          <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-widest text-neutral-500">
            <button onClick={() => setReasoning(v => !v)} className={`flex items-center gap-1 px-2 py-0.5 border ${reasoning ? "bg-[color:var(--hb-royal)] text-white border-[color:var(--hb-royal)]" : "border-neutral-300"}`} data-testid="reasoning-toggle">
              {reasoning ? <Brain size={10}/> : <Zap size={10}/>}
              {reasoning ? "Reasoning (gpt-4o)" : "Fast (gpt-4o-mini)"}
            </button>
            {lastUsage && (
              <span className="text-neutral-500" data-testid="last-usage">
                Last: {lastUsage.input}→{lastUsage.output} tok · ${lastUsage.cost_usd.toFixed(4)}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Ask anything — 'Give me 3 puff design ideas for a family reunion tee', etc."
              className="flex-1 border border-neutral-300 px-3 py-2 text-sm resize-none"
              rows={2}
              data-testid="chat-input"
            />
            <button
              onClick={send}
              disabled={busy || !input.trim()}
              className="bg-[color:var(--hb-navy)] text-white px-4 disabled:opacity-40 flex items-center justify-center"
              data-testid="chat-send"
            >
              {busy ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6"><Link to="/admin" className="text-xs text-[color:var(--hb-royal)] uppercase tracking-widest" data-testid="back-to-admin">← Admin home</Link></div>
    </div>
  );
}
